import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { setToastHandler } from './toastUtils';

const ToastContext = createContext(null);

const DEFAULT_AUTO_CLOSE = 3000;
const MAX_TOASTS_MOBILE = 3;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const removeToast = useCallback((id) => {
    // Clear timer if exists
    if (timersRef.current[id]) {
      clearTimeout(timersRef.current[id]);
      delete timersRef.current[id];
    }

    setToasts((prev) => prev.map((toast) =>
      toast.id === id ? { ...toast, isExiting: true } : toast
    ));

    // Remove from DOM after exit animation
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, 250);
  }, []);

  const addToast = useCallback((type, message, options = {}) => {
    const id = options.toastId || `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Helper function to create new toast array
    const createNewToastArray = (prev) => {
      const newToast = {
        id,
        type,
        message,
        isExiting: false,
        createdAt: Date.now(),
      };

      // Limit toasts on mobile
      const isMobile = window.innerWidth <= 768;
      let newToasts = [...prev, newToast];

      if (isMobile && newToasts.length > MAX_TOASTS_MOBILE) {
        // Remove oldest toasts
        const toRemove = newToasts.slice(0, newToasts.length - MAX_TOASTS_MOBILE);
        toRemove.forEach((t) => {
          if (timersRef.current[t.id]) {
            clearTimeout(timersRef.current[t.id]);
            delete timersRef.current[t.id];
          }
        });
        newToasts = newToasts.slice(-MAX_TOASTS_MOBILE);
      }

      return newToasts;
    };

    // If toastId provided and already exists, don't add duplicate
    if (options.toastId) {
      setToasts((prev) => {
        const existing = prev.find((t) => t.id === options.toastId);
        if (existing) return prev;
        return createNewToastArray(prev);
      });
    } else {
      setToasts((prev) => createNewToastArray(prev));
    }

    // Set auto-dismiss timer
    const autoClose = options.autoClose !== undefined ? options.autoClose : DEFAULT_AUTO_CLOSE;
    if (autoClose !== false && autoClose > 0) {
      timersRef.current[id] = setTimeout(() => {
        removeToast(id);
      }, autoClose);
    }

    return id;
  }, [removeToast]);

  // Register the toast handler for non-component usage
  useEffect(() => {
    setToastHandler({ addToast, removeToast });
    return () => setToastHandler(null);
  }, [addToast, removeToast]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach(clearTimeout);
    };
  }, []);

  const value = {
    toasts,
    addToast,
    removeToast,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToastContext must be used within a ToastProvider');
  }
  return context;
};

export default ToastContext;
